import {
    ColumnDef,
    ColumnOrderState,
    ColumnPinningState,
    ColumnSizingInfoState,
    ColumnSizingState,
    getCoreRowModel,
    getExpandedRowModel,
    RowData,
    RowSelectionState,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {hashQueryKey, useQuery} from "react-query";
import {
    createServerTableExpansionColumn,
    SERVER_TABLE_EXPANSION_COLUMN_ID
} from "./ServerTableExpansionColumn";
import {
    createServerTableSelectionColumn,
    SERVER_TABLE_SELECTION_COLUMN_ID
} from "./ServerTableSelectionColumn";
import {
    ServerTableAllRows,
    ServerTableController,
    ServerTableInstance,
    ServerTableQueryContext,
    ServerTableRowRange,
    UseServerTableOptions
} from "./types";
import {useServerTableQueryState} from "./useServerTableQueryState";
import {collectServerTableRows} from "./collectServerTableRows";
import {
    getStoredServerTableColumnPreferences,
    storeServerTableColumnPreferences
} from "./utils";

const EMPTY_DATA: never[] = [];

const ALL_ROWS_PAGE_SIZE = 200;

/** Guards against pulling an unbounded result set into the browser. */
const ALL_ROWS_MAX_ROWS = 5000;

/** Print cap for lists without `allRows`: more rows than this belong in an export. */
const PRINT_ROWS_MAX_ROWS = 1000;

/** Prefix used to invalidate every cached page of a table. */
export const serverTableQueryKey = (tableId: string) => ['server-table', tableId] as const;

const withUtilityColumnsFirst = (
    columnOrder: ColumnOrderState,
    utilityColumnIds: string[]
) => [
    ...utilityColumnIds,
    ...columnOrder.filter(columnId => !utilityColumnIds.includes(columnId))
];

const withUtilityColumnsPinned = (
    columnPinning: ColumnPinningState,
    utilityColumnIds: string[]
): ColumnPinningState => ({
    left: [
        ...utilityColumnIds,
        ...(columnPinning.left ?? []).filter(columnId => !utilityColumnIds.includes(columnId))
    ],
    right: (columnPinning.right ?? []).filter(columnId => !utilityColumnIds.includes(columnId))
});

const withoutUtilityColumns = (columnOrder: ColumnOrderState, utilityColumnIds: string[]) =>
    columnOrder.filter(columnId => !utilityColumnIds.includes(columnId));

const withoutPinnedUtilityColumns = (
    columnPinning: ColumnPinningState,
    utilityColumnIds: string[]
): ColumnPinningState => ({
    left: (columnPinning.left ?? []).filter(columnId => !utilityColumnIds.includes(columnId)),
    right: (columnPinning.right ?? []).filter(columnId => !utilityColumnIds.includes(columnId))
});

const getColumnId = <TData extends RowData>(column: ColumnDef<TData, any>) => {
    const accessorKey = 'accessorKey' in column ? String(column.accessorKey) : undefined;
    return column.id || accessorKey;
};

const getLeafColumns = <TData extends RowData>(
    columns: ColumnDef<TData, any>[]
): ColumnDef<TData, any>[] => columns.flatMap(column => (
    'columns' in column && Array.isArray(column.columns)
        ? getLeafColumns(column.columns)
        : [column]
));

/** Creates the controlled, server-driven controller consumed by ServerTableProvider. */
export const useServerTable = <TData extends RowData>({
    columns,
    getRowId,
    getSubRows,
    queryFn,
    queryDependencies = [],
    tableId,
    presentation = 'standard',
    enableColumnResizing,
    defaultPageSize = 10,
    initialSorting = [],
    urlState,
    enabled = true,
    refetchInterval = false,
    enableRowSelection = false,
    allRows,
    onRowClick
}: UseServerTableOptions<TData>): ServerTableController<TData> => {
    // The embedded preset fixes the presentation instead of persisting or sharing it: its
    // columns are chosen by the call site, and it must never own route parameters or write
    // preferences that a full-page table under the same `tableId` reads back.
    const embedded = presentation === 'embedded';
    const leafColumnDefinitions = getLeafColumns(columns);
    const {
        pagination,
        setPagination,
        replacePageIndex,
        searchQuery,
        setSearchQuery,
        filterRules,
        setFilterRules,
        sorting,
        setSorting
    } = useServerTableQueryState({
        defaultPageSize,
        initialSorting,
        presentation,
        urlState: embedded || urlState === false ? undefined : urlState ?? {}
    });
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const hasNestedRows = getSubRows !== undefined;
    const utilityColumnIds = [
        ...(enableRowSelection ? [SERVER_TABLE_SELECTION_COLUMN_ID] : []),
        ...(hasNestedRows ? [SERVER_TABLE_EXPANSION_COLUMN_ID] : [])
    ];
    const pinnedUtilityColumnIds = enableRowSelection
        ? [SERVER_TABLE_SELECTION_COLUMN_ID]
        : [];
    const tableColumns = useMemo(() => [
        ...(enableRowSelection ? [createServerTableSelectionColumn<TData>()] : []),
        ...(hasNestedRows ? [createServerTableExpansionColumn<TData>()] : []),
        ...columns
    ], [columns, enableRowSelection, hasNestedRows]);

    const [columnDefaults] = useState(() =>
        leafColumnDefinitions.reduce<{
            visibility: VisibilityState
            pinning: ColumnPinningState
            order: ColumnOrderState
        }>((defaults, column) => {
            const columnId = getColumnId(column);

            if (columnId) {
                defaults.order.push(columnId);
                const isVisible = column.meta?.defaultActive !== false;
                defaults.visibility[columnId] = isVisible;

                if (isVisible && column.meta?.defaultPinned) {
                    defaults.pinning.left?.push(columnId);
                }
            }

            return defaults;
        }, {visibility: {}, pinning: {left: [], right: []}, order: []})
    );
    const [storedColumnPreferences] = useState(() => embedded
        ? {
            columnVisibility: columnDefaults.visibility,
            columnPinning: columnDefaults.pinning,
            columnOrder: columnDefaults.order,
            columnSizing: {}
        }
        : getStoredServerTableColumnPreferences(tableId, {
            columnVisibility: columnDefaults.visibility,
            columnPinning: columnDefaults.pinning,
            columnOrder: columnDefaults.order,
            columnSizing: {}
        }));
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => ({
        ...storedColumnPreferences.columnVisibility
    }));
    const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(() => ({
        left: [...(storedColumnPreferences.columnPinning.left ?? [])],
        right: [...(storedColumnPreferences.columnPinning.right ?? [])]
    }));
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => [
        ...storedColumnPreferences.columnOrder
    ]);
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => ({
        ...storedColumnPreferences.columnSizing
    }));
    const tableColumnOrder = utilityColumnIds.length
        ? withUtilityColumnsFirst(columnOrder, utilityColumnIds)
        : columnOrder;
    const tableColumnPinning = pinnedUtilityColumnIds.length
        ? withUtilityColumnsPinned(columnPinning, pinnedUtilityColumnIds)
        : columnPinning;

    // Drag state stays controlled because body freezing and persistence depend on it.
    const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({
        startOffset: null,
        startSize: null,
        deltaOffset: null,
        deltaPercentage: null,
        isResizingColumn: false,
        columnSizingStart: []
    });

    // Persist the complete preference model only after a resize drag settles.
    useEffect(() => {
        if (embedded || columnSizingInfo.isResizingColumn) return;
        storeServerTableColumnPreferences(tableId, {
            columnVisibility,
            columnPinning,
            columnOrder,
            columnSizing
        });
    }, [
        embedded,
        tableId,
        columnVisibility,
        columnPinning,
        columnOrder,
        columnSizing,
        columnSizingInfo.isResizingColumn
    ]);

    const queryIdentity = hashQueryKey(['server-table-scope', tableId, queryDependencies]);
    const previousQueryIdentityRef = useRef(queryIdentity);
    const queryIdentityChanged = previousQueryIdentityRef.current !== queryIdentity;
    const queryPagination = queryIdentityChanged && pagination.pageIndex !== 0
        ? {...pagination, pageIndex: 0}
        : pagination;

    // A new dataset starts at page one in the same render that creates its query key.
    useLayoutEffect(() => {
        if (previousQueryIdentityRef.current === queryIdentity) return;

        previousQueryIdentityRef.current = queryIdentity;
        replacePageIndex(0);
    }, [queryIdentity, replacePageIndex]);

    const normalizedSearchQuery = searchQuery.trim();
    const sortedColumn = sorting[0]
        ? leafColumnDefinitions.find(column => getColumnId(column) === sorting[0].id)
        : undefined;
    const serverSorting = sorting[0] ? {
        field: sortedColumn?.meta?.serverSortField ?? sorting[0].id,
        direction: sorting[0].desc ? 'DESC' as const : 'ASC' as const
    } : undefined;

    const query = useQuery(
        [
            ...serverTableQueryKey(tableId),
            queryDependencies,
            queryPagination,
            normalizedSearchQuery,
            filterRules,
            serverSorting
        ],
        () => queryFn({
            pagination: queryPagination,
            searchQuery: normalizedSearchQuery,
            filterRules,
            sorting: serverSorting
        }),
        {enabled, keepPreviousData: true, refetchInterval}
    );

    const allRowsEnabled = !!allRows;
    const allRowsPageSize = allRows?.pageSize ?? ALL_ROWS_PAGE_SIZE;
    const allRowsMaxRows = allRows?.maxRows ?? ALL_ROWS_MAX_ROWS;
    const allRowsRequestRef = useRef<{
        queryFn: (context: ServerTableQueryContext) => Promise<{content: TData[], totalElements: number}>
        context: Omit<ServerTableQueryContext, 'pagination'>
    }>({
        queryFn,
        context: {searchQuery: normalizedSearchQuery, filterRules, sorting: serverSorting}
    });

    useEffect(() => {
        allRowsRequestRef.current = {
            queryFn,
            context: {searchQuery: normalizedSearchQuery, filterRules, sorting: serverSorting}
        };
    });

    const collectAllRows = useCallback(async (
        options?: Record<string, unknown>,
        range?: ServerTableRowRange
    ): Promise<ServerTableAllRows<TData>> => {
        const {queryFn: fetchPage, context} = allRowsRequestRef.current;
        return collectServerTableRows(
            pagination => fetchPage({...context, options, pagination}),
            {
                pageSize: allRowsPageSize,
                maxRows: allRowsMaxRows,
                getSubRows,
                offset: range?.offset,
                limit: range?.maxRows
            }
        );
    }, [allRowsMaxRows, allRowsPageSize, getSubRows]);

    // Every list prints beyond its page; only the `allRows` opt-ins keep their larger cap and start on All.
    const printMaxRows = allRowsEnabled ? allRowsMaxRows : PRINT_ROWS_MAX_ROWS;
    const collectPrintRows = useCallback(async (
        range?: {maxRows?: number}
    ): Promise<ServerTableAllRows<TData>> => {
        const {queryFn: fetchPage, context} = allRowsRequestRef.current;
        return collectServerTableRows(
            pagination => fetchPage({...context, pagination}),
            {pageSize: allRowsPageSize, maxRows: printMaxRows, getSubRows, limit: range?.maxRows}
        );
    }, [allRowsPageSize, getSubRows, printMaxRows]);
    const printRows = useMemo(
        () => ({fetch: collectPrintRows, maxRows: printMaxRows, defaultAll: allRowsEnabled}),
        [allRowsEnabled, collectPrintRows, printMaxRows]
    );

    useEffect(() => {
        if (!query.isSuccess || query.isPreviousData) return;

        const lastPageIndex = Math.max(
            Math.ceil(query.data.totalElements / pagination.pageSize) - 1,
            0
        );
        if (pagination.pageIndex <= lastPageIndex) return;

        replacePageIndex(lastPageIndex);
    }, [
        pagination.pageIndex,
        pagination.pageSize,
        query.data?.totalElements,
        query.isPreviousData,
        query.isSuccess,
        replacePageIndex
    ]);

    const table = useReactTable({
        data: query.data?.content ?? EMPTY_DATA,
        columns: tableColumns,
        getRowId,
        getSubRows,
        meta: {tableId},
        initialState: {
            columnVisibility: columnDefaults.visibility,
            columnPinning: pinnedUtilityColumnIds.length
                ? withUtilityColumnsPinned(columnDefaults.pinning, pinnedUtilityColumnIds)
                : columnDefaults.pinning,
            columnOrder: utilityColumnIds.length
                ? withUtilityColumnsFirst(columnDefaults.order, utilityColumnIds)
                : columnDefaults.order,
            sorting: initialSorting
        },
        state: {
            pagination: queryPagination,
            sorting,
            rowSelection,
            columnVisibility,
            columnPinning: tableColumnPinning,
            columnOrder: tableColumnOrder,
            columnSizing,
            columnSizingInfo
        },
        onPaginationChange: setPagination,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnPinningChange: updater => {
            setColumnPinning(current => {
                const currentTablePinning = pinnedUtilityColumnIds.length
                    ? withUtilityColumnsPinned(current, pinnedUtilityColumnIds)
                    : current;
                const next = typeof updater === 'function'
                    ? updater(currentTablePinning)
                    : updater;
                return pinnedUtilityColumnIds.length
                    ? withoutPinnedUtilityColumns(next, pinnedUtilityColumnIds)
                    : next;
            });
        },
        onColumnOrderChange: updater => {
            setColumnOrder(current => {
                const currentTableOrder = utilityColumnIds.length
                    ? withUtilityColumnsFirst(current, utilityColumnIds)
                    : current;
                const next = typeof updater === 'function'
                    ? updater(currentTableOrder)
                    : updater;
                return utilityColumnIds.length
                    ? withoutUtilityColumns(next, utilityColumnIds)
                    : next;
            });
        },
        onColumnSizingChange: setColumnSizing,
        onColumnSizingInfoChange: setColumnSizingInfo,
        // ServerTableHeader drives resizing; this flag lets column definitions opt out.
        enableColumnResizing: enableColumnResizing ?? !embedded,
        enableExpanding: hasNestedRows,
        enableRowSelection: enableRowSelection && !query.isPreviousData,
        enableMultiRowSelection: enableRowSelection && !query.isPreviousData,
        manualPagination: true,
        manualSorting: true,
        enableMultiSort: false,
        paginateExpandedRows: true,
        rowCount: query.data?.totalElements ?? 0,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: hasNestedRows ? getExpandedRowModel() : undefined
    }) as ServerTableInstance<TData>;

    return {
        table,
        query,
        presentation,
        searchQuery,
        isSearchActive: normalizedSearchQuery.length > 0,
        setSearchQuery,
        filterRules,
        setFilterRules,
        fetchAllRows: allRowsEnabled ? collectAllRows : undefined,
        printRows,
        onRowClick
    };
};
